import { z } from 'zod'
import { isValidCPF } from '../utils/formatDocuments'
z.setErrorMap((issue, ctx) => {
  if (issue.code === 'invalid_type' && issue.received === 'undefined') {
    return { message: 'Este campo é obrigatório' }
  }

  return { message: ctx.defaultError }
})

export const memberSchema = z.object({
  id: z.number().int().optional(),
  nome: z.string().min(3, 'O nome deve ter no mínimo 3 caracteres').trim(),
  foto: z.string().optional(),
  cpf: z
    .string()
    .regex(/^(\d{3}\.\d{3}\.\d{3}-\d{2})|(\d{11})$/, 'CPF inválido')
    .trim()
    .refine(isValidCPF, 'CPF inválido'),
  rg: z.string().min(8, 'O RG deve ter 8 caracteres').max(8, 'O RG deve ter 8 caracteres').regex(/^\d+$/, 'O RG deve conter apenas números').trim(),
  email: z.string().email('Email inválido').trim(),
  dataNascimento: z
    .string()
    .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Data de nascimento é obrigatória')
    .refine((value) => {
      const [day, month, year] = value.split('/').map(Number)
      const date = new Date(year, month - 1, day)
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    }, 'Data inválida'),
  sexo: z.string().optional(),
  estadoCivil: z.string().optional(),
  profissao: z.string().optional(),
  dataVencimentoExameMedico: z
    .string()
    .refine((value) => {
      if (!value) return true
      const [day, month, year] = value.split('/').map(Number)
      const date = new Date(year, month - 1, day)
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    }, 'Data inválida')
    .optional(),
  status: z.number().int(),
  consultor: z.object({
    id: z.number({ message: 'Selecione um consultor' }).int('Selecione um consultor'),
    nome: z.string().trim().min(2, 'Selecione um consultor'),
    email: z.string().email('Email inválido')
  }),
  endereco: z.object({
    numero: z.string().refine((value) => value.trim() !== '', 'Número não pode ser vazio'),
    bairro: z.string().refine((value) => value.trim() !== '', 'Bairro não pode ser vazio'),
    cidade: z.string().refine((value) => value.trim() !== '', 'Cidade não pode ser vazia'),
    rua: z.string().refine((value) => value.trim() !== '', 'Rua não pode ser vazia'),
    complemento: z.string().optional(),
    estado: z
      .string()
      .length(2, 'Estado deve conter 2 caracteres')
      .refine((value) => value.trim() !== '', 'Estado não pode ser vazio'),
    cep: z
      .string()
      .regex(/^\d{5}-\d{3}$/, 'CEP inválido')
      .refine((value) => value.trim() !== '', 'CEP não pode ser vazio')
  }),
  telefoneCelular: z.object({
    numero: z
      .string()
      .regex(/^\(\d{2}\) \d{4,5}-\d{4}$/, 'Telefone inválido')
      .refine((value) => value.trim() !== '', 'O telefone não pode ser vazio'),
    tipo: z.string().refine((value) => value.trim() !== '', 'Tipo não pode ser vazio')
  }),
  telefoneResidencial: z
    .object({
      numero: z.string().optional(),
      tipo: z.string().refine((value) => value.trim() !== '', 'Tipo não pode ser vazio')
    })
    .optional(),
  dadosEmergencia: z.object({
    contato: z.string().optional(),
    telefone: z.string().optional()
  }),
  responsavel: z
    .object({
      nome: z.string().optional(),
      telefone: z.string().optional(),
      documento: z.string()
      // .regex(/^(\d{3}\.\d{3}\.\d{3}-\d{2})|(\d{11})$/, "Documento inválido"),
    })
    .optional(),
  observacao: z.string().optional(),
  informacoesAdicionais: z
    .object({
      objetivoPrincipal: z.string().optional(),
      observacoes: z.string().optional()
    })
    .optional(),
  responsabilidade: z.object({
    indicacao: z.string().optional(),
    consultor: z.string().optional(),
    professor: z.string().optional()
  }),
  unidade: z.object({
    id: z.union([z.number().int(), z.string()])
  })
})

export type MemberGymFormData = z.infer<typeof memberSchema>

interface Address {
  estado: string
  cidade: string
  cep: string
  rua: string
  numero: string
  complemento: string
  bairro: string
}

interface Phone {
  id: number
  alteracao: string | null
  inclusao: string
  status: number
  ddd: number
  numero: number
  id_aluno: number
}

interface EmergencyContact {
  contato: string
  telefone: string
}

interface Consultant {
  id: number
  alteracao: string | null
  inclusao: string
  status: number
  nome: string
  foto: string | null
  cpf: string
  email: string
  sexo: string
  login: string
}

export interface Student {
  id: number
  alteracao: string | null
  inclusao: string
  status: number
  nome: string
  foto: string
  cpf: string
  rg: string | null
  email: string
  dataNascimento: string
  sexo: string
  estadoCivil: string | null
  profissao: string | null
  dataVencimentoExameMedico: string
  consultor: Consultant | null
  endereco: Address
  telefone: Phone[]
  dadosEmergencia: EmergencyContact[]
  responsavel: string | null
  ultimoPagamento: string | null
  observacao: string | null
  informacoesAdicionais: string | null
  responsabilidade: string | null
  unidade: {
    id: number
  }
}
